import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RestApiBase } from '../shared/configurations/rest-api-base.config';
import { UrlUtils } from '../shared/utils/url.utils';
import { TranslateService } from '@ngx-translate/core';
import { SearchEntities } from '../shared/configurations/portal-search.constant';
import { ConfigurationHandlerService } from '../core/configuration-handler.service';
import { shareReplay, startWith, take, tap } from 'rxjs';
import { AcademicUnitPortalDTO } from '../shared/external-model/academicunit/academic-unit-portal-dto';
import { AssetsUsage } from '../shared/interfaces/assets-usage';

@Injectable({
    providedIn: 'root',
})
export class ResearchUnitsService {
    private http = inject(HttpClient);
    private translate = inject(TranslateService);
    private configurationHandlerService = inject(ConfigurationHandlerService);

    institutionCode = UrlUtils.getParam('institution');
    descriptionsCache = {};
    linksCache = {};

    homepageOrgUnitCode = signal<string>(undefined);

    get configSections() {
        return this.configurationHandlerService
            .portalOrgTreeConfig()
            ?.sections?.filter((section) => section.active);
    }

    get configHomepageSections() {
        return this.configurationHandlerService
            .portalOrgTreeConfig()
            ?.homepageSections?.filter((section) => section.active);
    }

    get defaultSections() {
        const generalConfig =
            this.configurationHandlerService.portalOrgTreeConfig()
                ?.generalConfiguration;

        if (generalConfig?.defaultTab) {
            const defaultTab = generalConfig?.defaultTab.replace('tab.', '');
            return [defaultTab, 'outputs'];
        } else if (generalConfig?.defaultTabResearchers) {
            return ['researchers', 'outputs'];
        } else {
            return ['outputs', 'researchers'];
        }
    }

    getOrgFirstLevelSubUnits(organizationCode: string) {
        const params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('organizationCode', organizationCode);
        return this.http.get(
            RestApiBase.NEW_ORGANIZATION_FIRST_LEVEL_SUB_UNITS,
            { params },
        );
    }

    getOrgAncestors(organizationCode: string) {
        const params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('organizationCode', organizationCode);
        return this.http.get(RestApiBase.NEW_ORGANIZATION_ANCESTORS, {
            params,
        });
    }

    getResearchUnitByCode(code: string) {
        const params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('academicUnitCode', code);
        return this.http
            .get<AcademicUnitPortalDTO>(
                RestApiBase.API_ENDPOINT_ORG_UNITS + `/getByCode`,
                {
                    params,
                },
            )
            .pipe(take(1));
    }

    getResearchUnitFiles(id: string) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        return this.http
            .get<any[]>(RestApiBase.API_ENDPOINT_ORG_UNITS + `/${id}/files`, {
                params,
            })
            .pipe(take(1));
    }

    getResearchUnitLinks(id: string) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        const observable = this.http
            .get<any[]>(RestApiBase.API_ENDPOINT_ORG_UNITS + `/${id}/links`, {
                params,
            })
            .pipe(
                tap((links) => {
                    this.linksCache[id] = links;
                }),
            );
        if (this.linksCache[id] && this.linksCache[id].length > 0) {
            return observable.pipe(startWith(this.linksCache[id]));
        } else {
            return observable;
        }
    }

    getResearchUnitDescriptions(id: string) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        const observable = this.http
            .get<any[]>(
                RestApiBase.API_ENDPOINT_ORG_UNITS + `/${id}/descriptions`,
                {
                    params,
                },
            )
            .pipe(
                tap((descriptions) => {
                    this.descriptionsCache[id] = descriptions;
                }),
            );

        if (
            this.descriptionsCache[id] &&
            this.descriptionsCache[id].length > 0
        ) {
            return observable.pipe(startWith(this.descriptionsCache[id]));
        } else {
            return observable;
        }
    }

    getOrgUnitMetrics(unitId: string) {
        const params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('unit', unitId);
        return this.http
            .get<AssetsUsage>(RestApiBase.GET_TOTAL_ASSETS_USAGE, { params })
            .pipe(shareReplay());
    }

    getOrgUnits() {
        const params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('language', this.translate.currentLang);
        return this.http.get(RestApiBase.NEW_ORGANIZATION_TREE, { params });
    }

    alphabeticallySortAfterTranslation(a, b) {
        if (this.translate.instant(a) < this.translate.instant(b)) {
            return -1;
        }
        if (this.translate.instant(a) > this.translate.instant(b)) {
            return 1;
        }
        return 0;
    }

    unitLinkQueryParams(unit, entity: string) {
        const queryParams = {
            unit: unit,
            page: 1,
            institution: this.institutionCode,
        };
        if (entity === SearchEntities.OUTPUTS_ENTITY) {
            queryParams['sort'] = 'date_d';
        } else if (entity === SearchEntities.RESEARCHERS_ENTITY) {
            queryParams['query'] = '*';
            queryParams['scope'] = 'all';
        } else if (entity !== SearchEntities.PROJECTS_ENTITY) {
            delete queryParams['unit'];
            delete queryParams['page'];
        }
        return queryParams;
    }

    isProfileUnitTabEnabled() {
        return (
            this.configurationHandlerService.isProfileActivate() &&
            this.configurationHandlerService.getCustomerParameter(
                'esploro_org_unit_profiles_tab',
            ) === 'true'
        );
    }
}
